// src/components/AnimatedBackground.jsx

import React, { useEffect, useRef } from 'react';

const AnimatedBackground = () => {
  const bgRef      = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const canvas  = bgRef.current;
    const ctx     = canvas?.getContext('2d');
    const overlay = overlayRef.current;
    const octx    = overlay?.getContext('2d');
    if (!ctx || !octx) return;

    // —— 状态 ——  
    let stars = [], shootingStars = [], nebulas = [];
    let trails = [], constellations = [];
    let animId;

    // —— 26 个真实星座的连接模式 ——  
    // 坐标均为 [0,1] 归一化
    const constellationPatterns = [
      // 1. Orion
      [
        { x: 0.45, y: 0.20 },
        { x: 0.50, y: 0.35 },
        { x: 0.55, y: 0.20 },
        { x: 0.58, y: 0.45 },
        { x: 0.40, y: 0.60 },
        { x: 0.62, y: 0.65 },
      ],
      // 2. Ursa Major
      [
        { x: 0.20, y: 0.30 },
        { x: 0.25, y: 0.25 },
        { x: 0.35, y: 0.20 },
        { x: 0.45, y: 0.25 },
        { x: 0.55, y: 0.30 },
        { x: 0.65, y: 0.40 },
        { x: 0.60, y: 0.50 },
        { x: 0.50, y: 0.45 },
      ],
      // 3. Cassiopeia
      [
        { x: 0.75, y: 0.20 },
        { x: 0.70, y: 0.30 },
        { x: 0.80, y: 0.35 },
        { x: 0.68, y: 0.45 },
        { x: 0.78, y: 0.55 },
      ],
      // 4. Cygnus
      [
        { x: 0.40, y: 0.40 },
        { x: 0.45, y: 0.30 },
        { x: 0.50, y: 0.40 },
        { x: 0.55, y: 0.50 },
        { x: 0.60, y: 0.60 },
      ],
      // 5. Leo
      [
        { x: 0.30, y: 0.20 },
        { x: 0.35, y: 0.25 },
        { x: 0.40, y: 0.30 },
        { x: 0.45, y: 0.35 },
        { x: 0.50, y: 0.40 },
      ],
      // 6. Taurus
      [
        { x: 0.60, y: 0.30 },
        { x: 0.65, y: 0.35 },
        { x: 0.70, y: 0.40 },
        { x: 0.75, y: 0.45 },
        { x: 0.80, y: 0.50 },
      ],
      // 7. Scorpius
      [
        { x: 0.20, y: 0.60 },
        { x: 0.25, y: 0.65 },
        { x: 0.30, y: 0.70 },
        { x: 0.35, y: 0.75 },
        { x: 0.40, y: 0.80 },
        { x: 0.45, y: 0.85 },
      ],
      // 8. Sagittarius
      [
        { x: 0.55, y: 0.55 },
        { x: 0.60, y: 0.60 },
        { x: 0.65, y: 0.65 },
        { x: 0.70, y: 0.60 },
        { x: 0.75, y: 0.55 },
      ],
      // 9. Gemini
      [
        { x: 0.30, y: 0.40 },
        { x: 0.35, y: 0.50 },
        { x: 0.40, y: 0.40 },
        { x: 0.45, y: 0.50 },
      ],
      // 10. Aquarius
      [
        { x: 0.15, y: 0.30 },
        { x: 0.20, y: 0.35 },
        { x: 0.25, y: 0.30 },
        { x: 0.30, y: 0.35 },
        { x: 0.35, y: 0.30 },
      ],
      // 11. Pisces
      [
        { x: 0.65, y: 0.20 },
        { x: 0.70, y: 0.25 },
        { x: 0.75, y: 0.20 },
        { x: 0.80, y: 0.25 },
      ],
      // 12. Virgo
      [
        { x: 0.40, y: 0.30 },
        { x: 0.45, y: 0.35 },
        { x: 0.50, y: 0.30 },
        { x: 0.55, y: 0.35 },
        { x: 0.60, y: 0.30 },
      ],
      // 13. Cancer
      [
        { x: 0.25, y: 0.45 },
        { x: 0.30, y: 0.50 },
        { x: 0.35, y: 0.45 },
        { x: 0.40, y: 0.50 },
      ],
      // 14. Aries
      [
        { x: 0.50, y: 0.15 },
        { x: 0.55, y: 0.20 },
        { x: 0.60, y: 0.15 },
      ],
      // 15. Capricornus
      [
        { x: 0.70, y: 0.70 },
        { x: 0.75, y: 0.75 },
        { x: 0.80, y: 0.80 },
        { x: 0.85, y: 0.75 },
      ],
      // 16. Libra
      [
        { x: 0.45, y: 0.55 },
        { x: 0.50, y: 0.60 },
        { x: 0.55, y: 0.55 },
      ],
      // 17. Perseus
      [
        { x: 0.60, y: 0.10 },
        { x: 0.65, y: 0.15 },
        { x: 0.70, y: 0.10 },
      ],
      // 18. Andromeda
      [
        { x: 0.80, y: 0.10 },
        { x: 0.85, y: 0.15 },
        { x: 0.90, y: 0.20 },
        { x: 0.95, y: 0.25 },
      ],
      // 19. Pegasus
      [
        { x: 0.75, y: 0.25 },
        { x: 0.80, y: 0.30 },
        { x: 0.85, y: 0.35 },
        { x: 0.90, y: 0.30 },
      ],
      // 20. Leo Minor
      [
        { x: 0.35, y: 0.15 },
        { x: 0.40, y: 0.20 },
        { x: 0.45, y: 0.15 },
      ],
      // 21. Monoceros
      [
        { x: 0.50, y: 0.40 },
        { x: 0.55, y: 0.45 },
        { x: 0.60, y: 0.40 },
      ],
      // 22. Canis Major
      [
        { x: 0.55, y: 0.50 },
        { x: 0.60, y: 0.55 },
        { x: 0.65, y: 0.50 },
      ],
      // 23. Canis Minor
      [
        { x: 0.52, y: 0.45 },
        { x: 0.57, y: 0.48 },
      ],
      // 24. Draco
      [
        { x: 0.10, y: 0.10 },
        { x: 0.15, y: 0.15 },
        { x: 0.20, y: 0.10 },
        { x: 0.25, y: 0.15 },
        { x: 0.30, y: 0.10 },
      ],
      // 25. Cetus
      [
        { x: 0.65, y: 0.60 },
        { x: 0.70, y: 0.65 },
        { x: 0.75, y: 0.60 },
      ],
      // 26. Lyra
      [
        { x: 0.60, y: 0.15 },
        { x: 0.62, y: 0.18 },
        { x: 0.64, y: 0.16 },
        { x: 0.66, y: 0.19 },
      ],
    ];

    // —— 尺寸调整 ——  
    function resize() {
      canvas.width   = window.innerWidth;
      canvas.height  = window.innerHeight;
      overlay.width  = window.innerWidth;
      overlay.height = window.innerHeight;
    }
    window.addEventListener('resize', resize);
    resize();

    // —— 初始化星星 & 星云 & 流星 ——  
    function initStars() {
      stars = Array.from({ length: 200 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.5,
        alpha: Math.random(),
        dAlpha: (Math.random() * 0.02 + 0.005) * (Math.random() < 0.5 ? 1 : -1),
      }));
    }
    function initNebulas() {
      const colors = [
        'rgba(255,0,255,0.2)',
        'rgba(0,255,255,0.2)',
        'rgba(255,255,255,0.15)',
      ];
      nebulas = Array.from({ length: 5 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 200 + 150,
        color: colors[Math.floor(Math.random() * colors.length)],
        dx: (Math.random() - 0.5) * 0.2,
        dy: (Math.random() - 0.5) * 0.2,
      }));
    }
    function spawnShootingStar() {
      shootingStars.push({
        x: Math.random() * canvas.width * 0.8,
        y: Math.random() * (canvas.height * 0.2) + canvas.height * 0.1,
        len: Math.random() * 300 + 100,
        speed: Math.random() * 10 + 5,
        angle: -Math.PI / 4,
        alpha: 1,
      });
      setTimeout(spawnShootingStar, Math.random() * 4000 + 2000);
    }

    // —— 流光拖尾 ——  
    function spawnTrail(x, y) {
      trails.push({ x, y, vx: (Math.random() - 0.5) * 1, vy: (Math.random() - 0.5) * 1, life: 0, ttl: 60, size: Math.random() * 2 + 1 });
    }
    window.addEventListener('mousemove', e => spawnTrail(e.clientX, e.clientY));
    window.addEventListener('click',     e => spawnTrail(e.clientX, e.clientY));

    // —— 现实星座连线 (一次生成两种星座) ——  
    function spawnConstellation() {
      const indices = [];
      while (indices.length < 2) {
        const idx = Math.floor(Math.random() * constellationPatterns.length);
        if (!indices.includes(idx)) indices.push(idx);
      }
      indices.forEach(i => {
        const pattern = constellationPatterns[i];
        const pts = pattern.map(p => ({
          x: p.x * canvas.width,
          y: p.y * canvas.height,
        }));
        for (let j = 0; j < pts.length - 1; j++) {
          const a = pts[j], b = pts[j + 1];
          constellations.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, life: 0, ttl: 120 });
        }
      });
    }
    const constellationInterval = setInterval(spawnConstellation, 4000);

    // —— 绘制帮助函数 ——  
    function drawNebulas() {
      nebulas.forEach(n => {
        n.x += n.dx; n.y += n.dy;
        if (n.x < -n.r) n.x = canvas.width + n.r;
        if (n.x > canvas.width + n.r) n.x = -n.r;
        if (n.y < -n.r) n.y = canvas.height + n.r;
        if (n.y > canvas.height + n.r) n.y = -n.r;
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
        grad.addColorStop(0, n.color);
        grad.addColorStop(1, 'rgba(10,10,25,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(n.x - n.r, n.y - n.r, n.r * 2, n.r * 2);
      });
    }
    function drawStars() {
      stars.forEach(s => {
        s.alpha += s.dAlpha;
        if (s.alpha <= 0 || s.alpha >= 1) s.dAlpha *= -1;
        ctx.fillStyle = `rgba(255,255,255,${s.alpha.toFixed(2)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fill();
      });
    }
    function drawShootingStars() {
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const m = shootingStars[i];
        const dx = Math.cos(m.angle) * m.speed;
        const dy = Math.sin(m.angle) * m.speed;
        ctx.strokeStyle = `rgba(255,255,255,${m.alpha.toFixed(2)})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(m.x, m.y);
        ctx.lineTo(m.x + (dx * m.len) / m.speed, m.y + (dy * m.len) / m.speed);
        ctx.stroke();
        m.x += dx; m.y += dy; m.alpha -= 0.005;
        if (m.alpha <= 0) shootingStars.splice(i, 1);
      }
    }
    function updateAndDrawTrails() {
      octx.clearRect(0, 0, overlay.width, overlay.height);
      for (let i = trails.length - 1; i >= 0; i--) {
        const p = trails[i];
        p.x += p.vx; p.y += p.vy; p.life++;
        const alpha = 1 - p.life / p.ttl;
        if (alpha <= 0) { trails.splice(i, 1); continue; }
        octx.beginPath();
        octx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        octx.fillStyle = `rgba(255,255,255,${alpha.toFixed(2)})`;
        octx.fill();
      }
    }
    function updateAndDrawConstellations() {
      for (let i = constellations.length - 1; i >= 0; i--) {
        const seg = constellations[i];
        seg.life++;
        const half = seg.ttl / 2;
        const alpha = seg.life <= half
          ? seg.life / half
          : 1 - (seg.life - half) / half;
        if (seg.life >= seg.ttl) { constellations.splice(i, 1); continue; }
        octx.beginPath();
        octx.moveTo(seg.x1, seg.y1);
        octx.lineTo(seg.x2, seg.y2);
        octx.strokeStyle = `rgba(255,255,255,${(alpha * 0.5).toFixed(2)})`;
        octx.lineWidth = 1;
        octx.stroke();
      }
    }

    // —— 主动画循环 ——  
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawNebulas();
      drawStars();
      drawShootingStars();
      updateAndDrawTrails();
      updateAndDrawConstellations();
      animId = requestAnimationFrame(animate);
    }

    // —— 启动所有流程 ——  
    initStars();
    initNebulas();
    spawnShootingStar();
    animate();

    // —— 清理 ——  
    return () => {
      cancelAnimationFrame(animId);
      clearInterval(constellationInterval);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <>
      <canvas
        ref={bgRef}
        id="bg"
        className="fixed top-0 left-0 w-screen h-screen -z-2"
      />
      <canvas
        ref={overlayRef}
        id="overlay"
        className="fixed top-0 left-0 w-screen h-screen -z-1 pointer-events-none"
      />
    </>
  );
};

export default AnimatedBackground;
